import type { FileEntry } from "@zip.js/zip.js";
import * as OTPAuth from "otpauth";
import type { FileDestination } from "../../types/components";
import type {
	ImportCustomField,
	ImportedSecret,
	ImportItemsFolder,
	ImportResult,
} from "../../types/import";
import { uploadAttachmentStream } from "./attachment";
import cryptoLibrary from "./crypto-library";
import fileTransfer from "./file-transfer";
import helperService from "./helper";
import secretService from "./secret";

const wrongFormat = () => ({ errors: ["FILE_FORMAT_WRONG"] });

export interface ZipAttachment {
	entry: FileEntry;
	secret: ImportedSecret;
	filename: string;
}

interface ZipManifest {
	encrypted: boolean;
	items: { id?: string; attachments?: { id?: string; fileName?: string }[] }[];
}

/** Only the directory and JSON are held in memory, never attachment contents. */
export async function openBitwardenZip(file: Blob) {
	const { BlobReader, Uint8ArrayWriter, ZipReader } = await import(
		"@zip.js/zip.js"
	);
	const reader = new ZipReader(new BlobReader(file), { useWebWorkers: false });
	try {
		const entries = await reader.getEntries();
		const manifests = entries.filter(
			(entry) => !entry.directory && entry.filename === "data.json",
		);
		if (manifests.length !== 1) throw wrongFormat();
		const manifest = manifests[0] as FileEntry;
		if (manifest.encrypted) throw wrongFormat();
		const json = new TextDecoder().decode(
			await manifest.getData(new Uint8ArrayWriter(), { checkSignature: true }),
		);
		const raw: ZipManifest = JSON.parse(json);
		if (raw.encrypted !== false || !Array.isArray(raw.items))
			throw wrongFormat();
		const byId = new Map<string, ImportedSecret[]>();
		const byName = new Map<string, ImportedSecret[]>();
		const sources = new Map(raw.items.map((item) => [item.id, item]));
		const add = (
			index: Map<string, ImportedSecret[]>,
			key: string | undefined,
			secret: ImportedSecret,
		) => {
			if (key) index.set(key, [...(index.get(key) ?? []), secret]);
		};
		const parsed = parseBitwardenZipJson(json, (source, secret) => {
			add(byId, source.id, secret);
			add(byName, source.name, secret);
		});
		if (!parsed) throw wrongFormat();
		const orphanNotes = new Map<string, ImportedSecret>();
		const attachments: ZipAttachment[] = [];
		const paths = new Set<string>();
		for (const entry of entries) {
			if (entry.directory) continue;
			if (paths.has(entry.filename)) throw wrongFormat();
			paths.add(entry.filename);
			if (!entry.filename.startsWith("attachments/")) continue;
			const parts = entry.filename.split("/");
			if (
				entry.encrypted ||
				parts.length < 3 ||
				parts.some((part) => !part || part === "." || part === "..")
			)
				throw wrongFormat();
			const owner = parts.slice(1, -1).join("/");
			const candidates = byId.get(owner) ?? byName.get(owner) ?? [];
			if (candidates.length > 1) throw wrongFormat();
			let secret = candidates[0] ?? orphanNotes.get(owner);
			if (!secret) {
				secret = {
					id: cryptoLibrary.generateUuid(),
					type: "note",
					name: owner,
					note_title: owner,
					note_notes: "",
				};
				parsed.datastore.items.push(secret);
				parsed.secrets.push(secret);
				orphanNotes.set(owner, secret);
			}
			// ID-based exports may use attachment IDs as the ZIP filename.
			const source = sources.get(owner);
			const metadata = source?.attachments?.find(
				(attachment: { id?: string }) =>
					attachment.id === parts[parts.length - 1],
			);
			attachments.push({
				entry: entry as FileEntry,
				secret,
				filename: metadata?.fileName ?? parts[parts.length - 1],
			});
		}
		return { reader, parsed: parsed as ImportResult, attachments };
	} catch (error) {
		await reader.close();
		throw wrongFormat();
	}
}

export async function importZipAttachments(
	attachments: ZipAttachment[],
	destination: FileDestination,
	onProgress: (percentage: number) => void,
) {
	for (let index = 0; index < attachments.length; index++) {
		const { entry, secret, filename } = attachments[index];
		const attachment = await uploadAttachmentStream(
			entry.uncompressedSize,
			filename,
			destination,
			secret.secret_id!,
			async (write) => {
				await entry.getData(new WritableStream({ write }), {
					checkSignature: true,
				});
			},
			(percentage) =>
				onProgress(((index + percentage / 100) / attachments.length) * 100),
		);
		try {
			const content = await secretService.readSecret(
				secret.secret_id!,
				secret.secret_key!,
			);
			content.attachments = [...(content.attachments ?? []), attachment];
			const result = await secretService.writeSecret(
				secret.secret_id!,
				secret.secret_key!,
				content,
			);
			if (!result) throw new Error("Attachment metadata could not be saved");
		} catch (error) {
			await fileTransfer.deleteFile(attachment.file_id).catch(() => {});
			throw error;
		}
	}
}

// Intentionally duplicated from the JSON importer so ZIP parsing can evolve independently.
interface BitwardenItem {
	id?: string;
	type: number;
	name?: string | null;
	notes?: string | null;
	folderId?: string | null;
	login?: {
		username?: string | null;
		password?: string | null;
		uris?: { uri: string }[] | null;
		totp?: string | null;
	} | null;
	fields?: { name: string | null; type: number; value: string | null }[] | null;
	card?: {
		cardholderName?: string | null;
		valuecardholderName?: string | null;
		brand?: string | null;
		number?: string | null;
		expMonth?: string | null;
		expYear?: string | null;
		code?: string | null;
	} | null;
	identity?: Partial<
		Record<
			| "title"
			| "firstName"
			| "middleName"
			| "lastName"
			| "address1"
			| "address2"
			| "address3"
			| "city"
			| "state"
			| "postalCode"
			| "country"
			| "company"
			| "email"
			| "phone"
			| "ssn"
			| "username"
			| "passportNumber"
			| "licenseNumber",
			string | null
		>
	> | null;
	sshKey?: {
		publicKey?: string | null;
		privateKey?: string | null;
		keyFingerprint?: string | null;
	} | null;
}

interface BitwardenExport {
	folders?: { id: string; name: string }[];
	items?: BitwardenItem[];
}

type BitwardenRoot = ImportItemsFolder & { folders: ImportItemsFolder[] };

function parseTotp(value: string): OTPAuth.TOTP | OTPAuth.HOTP {
	const key = value.trim();
	return key.startsWith("otpauth://")
		? OTPAuth.URI.parse(key)
		: new OTPAuth.TOTP({ secret: key.replace(/\s/g, "").toUpperCase() });
}

/**
 * Analyzes an item and return its types
 *
 * @param {object} item The item to analyze
 */
function detect_type(item: BitwardenItem) {
	if (item["type"] === 1) {
		// real website password
		return "website_password";
	}
	if (item["type"] === 2) {
		// real note
		return "note";
	}
	if (item["type"] === 3) {
		// real credit card
		return "credit_card";
	}
	if (item["type"] === 4) {
		// actually an identity but we map it to a note
		return "note";
	}
	if (item["type"] === 5) {
		// real SSH key
		return "ssh_own_key";
	}

	return "note";
}

/**
 * Checks whether the provided item contains in addition a totp code
 *
 * @param {object} item The item to analyze
 */
function containsTotp(item: BitwardenItem) {
	if (
		item.login != null &&
		Object.hasOwn(item["login"], "totp") &&
		item["login"]["totp"] != null
	) {
		return true;
	}
	return false;
}

/**
 * Takes an item and transforms it into a website password entry
 *
 * @param {[]} item One item of the json
 *
 * @returns {*} The secrets object
 */
function transformToWebsitePassword(item: BitwardenItem): ImportedSecret {
	let name = "";
	let urlfilter = "";
	let website_password_url_filter = "";
	let website_password_password = "";
	let website_password_username = "";
	let website_password_notes = "";
	let website_password_url = "";
	let website_password_title = "";
	const custom_fields: ImportCustomField[] = [];

	if (Object.hasOwn(item, "name") && item["name"] != null) {
		name = item["name"];
		website_password_title = item["name"];
	}
	if (Object.hasOwn(item, "notes") && item["notes"] != null) {
		website_password_notes = item["notes"];
	}
	if (Object.hasOwn(item, "login") && item["login"] != null) {
		if (
			Object.hasOwn(item["login"], "username") &&
			item["login"]["username"] != null
		) {
			website_password_username = item["login"]["username"];
		}
		if (
			Object.hasOwn(item["login"], "password") &&
			item["login"]["password"] != null
		) {
			website_password_password = item["login"]["password"];
		}
		if (
			Object.hasOwn(item["login"], "uris") &&
			item["login"]["uris"] != null &&
			item["login"]["uris"].length > 0
		) {
			website_password_url = item["login"]["uris"][0]["uri"];

			const parsed_url = helperService.parseUrl(website_password_url);
			if (parsed_url.authority !== null) {
				urlfilter = parsed_url.authority;
				website_password_url_filter = parsed_url.authority;
			}
			if (item["login"]["uris"].length > 1) {
				for (let i = 1; i < item["login"]["uris"].length; i++) {
					const parsed_url = helperService.parseUrl(website_password_url);
					if (parsed_url.authority !== null) {
						urlfilter = urlfilter + ", " + parsed_url.authority;
						website_password_url_filter =
							website_password_url_filter + ", " + parsed_url.authority;
					}
				}
			}
		}
	}

	if (Object.hasOwn(item, "fields") && item["fields"] != null) {
		for (let i = 0; i < item["fields"].length; i++) {
			custom_fields.push({
				name: item["fields"][i]["name"] ?? "",
				type: item["fields"][i]["type"] === 0 ? "text" : "password",
				value: item["fields"][i]["value"] ?? "",
			});
		}
	}

	const websitePassword: ImportedSecret = {
		id: cryptoLibrary.generateUuid(),
		type: "website_password",
		name: name,
		description: website_password_username,
		urlfilter: urlfilter,
		website_password_url_filter: website_password_url_filter,
		website_password_password: website_password_password,
		website_password_username: website_password_username,
		website_password_notes: website_password_notes,
		website_password_url: website_password_url,
		website_password_title: website_password_title,
		custom_fields: custom_fields,
	};
	if (item.login?.totp) {
		try {
			const parsedTotp = parseTotp(item.login.totp);
			if (!parsedTotp.secret.base32) throw new Error("Missing TOTP secret");
			websitePassword.website_password_totp_code = parsedTotp.secret.base32;
			websitePassword.website_password_totp_algorithm = parsedTotp.algorithm;
			websitePassword.website_password_totp_digits = parsedTotp.digits;
			websitePassword.website_password_totp_period =
				parsedTotp instanceof OTPAuth.TOTP ? parsedTotp.period : undefined;
		} catch (e) {
			const notes = websitePassword.website_password_notes || "";
			websitePassword.website_password_notes =
				notes +
				(notes && !notes.endsWith("\n") ? "\n" : "") +
				"TOTP: " +
				item.login.totp;
		}
	}
	return websitePassword;
}

/**
 * Takes an item and transforms it into a note
 *
 * @param {[]} item One item of the json
 *
 * @returns {*} The secrets object
 */
function transformToNote(item: BitwardenItem): ImportedSecret {
	let name = "";
	let note_notes = "";
	let note_title = "";
	const custom_fields: ImportCustomField[] = [];

	if (Object.hasOwn(item, "name") && item["name"] != null) {
		name = item["name"];
		note_title = item["name"];
	}

	if (
		Object.hasOwn(item, "notes") &&
		item["notes"] !== null &&
		item["notes"] !== ""
	) {
		note_notes = item["notes"] + "\n";
	}

	if (Object.hasOwn(item, "fields") && item["fields"] != null) {
		for (let i = 0; i < item["fields"].length; i++) {
			custom_fields.push({
				name: item["fields"][i]["name"] ?? "",
				type: item["fields"][i]["type"] === 0 ? "text" : "password",
				value: item["fields"][i]["value"] ?? "",
			});
		}
	}

	if (Object.hasOwn(item, "card") && item["card"] != null) {
		note_notes =
			note_notes + "Name" + ": " + item["card"]["valuecardholderName"] + "\n";
		note_notes = note_notes + "Brand" + ": " + item["card"]["brand"] + "\n";
		note_notes = note_notes + "Number" + ": " + item["card"]["number"] + "\n";
		note_notes = note_notes + "Month" + ": " + item["card"]["expMonth"] + "\n";
		note_notes = note_notes + "Year" + ": " + item["card"]["expYear"] + "\n";
		note_notes = note_notes + "Code" + ": " + item["card"]["code"] + "\n";
	}

	if (Object.hasOwn(item, "identity") && item["identity"] != null) {
		if (item["identity"]["title"] !== null) {
			note_notes =
				note_notes + "Title" + ": " + item["identity"]["title"] + "\n";
		}
		if (item["identity"]["firstName"] !== null) {
			note_notes =
				note_notes + "Firstname" + ": " + item["identity"]["firstName"] + "\n";
		}
		if (item["identity"]["middleName"] !== null) {
			note_notes =
				note_notes +
				"Middlename" +
				": " +
				item["identity"]["middleName"] +
				"\n";
		}
		if (item["identity"]["lastName"] !== null) {
			note_notes =
				note_notes + "Lastname" + ": " + item["identity"]["lastName"] + "\n";
		}
		if (item["identity"]["address1"] !== null) {
			note_notes =
				note_notes + "Address1" + ": " + item["identity"]["address1"] + "\n";
		}
		if (item["identity"]["address2"] !== null) {
			note_notes =
				note_notes + "Address2" + ": " + item["identity"]["address2"] + "\n";
		}
		if (item["identity"]["address3"] !== null) {
			note_notes =
				note_notes + "Address3" + ": " + item["identity"]["address3"] + "\n";
		}
		if (item["identity"]["city"] !== null) {
			note_notes = note_notes + "City" + ": " + item["identity"]["city"] + "\n";
		}
		if (item["identity"]["state"] !== null) {
			note_notes =
				note_notes + "State" + ": " + item["identity"]["state"] + "\n";
		}
		if (item["identity"]["postalCode"] !== null) {
			note_notes =
				note_notes +
				"Postal Code" +
				": " +
				item["identity"]["postalCode"] +
				"\n";
		}
		if (item["identity"]["country"] !== null) {
			note_notes =
				note_notes + "Country" + ": " + item["identity"]["country"] + "\n";
		}
		if (item["identity"]["company"] !== null) {
			note_notes =
				note_notes + "Company" + ": " + item["identity"]["company"] + "\n";
		}
		if (item["identity"]["email"] !== null) {
			note_notes =
				note_notes + "Email" + ": " + item["identity"]["email"] + "\n";
		}
		if (item["identity"]["phone"] !== null) {
			note_notes =
				note_notes + "Phone" + ": " + item["identity"]["phone"] + "\n";
		}
		if (item["identity"]["ssn"] !== null) {
			note_notes = note_notes + "SSN" + ": " + item["identity"]["ssn"] + "\n";
		}
		if (item["identity"]["username"] !== null) {
			note_notes =
				note_notes + "Username" + ": " + item["identity"]["username"] + "\n";
		}
		if (item["identity"]["passportNumber"] !== null) {
			note_notes =
				note_notes +
				"Passport Number" +
				": " +
				item["identity"]["passportNumber"] +
				"\n";
		}
		if (item["identity"]["licenseNumber"] !== null) {
			note_notes =
				note_notes +
				"License Number" +
				": " +
				item["identity"]["licenseNumber"] +
				"\n";
		}
	}

	return {
		id: cryptoLibrary.generateUuid(),
		type: "note",
		name: name,
		note_notes: note_notes,
		note_title: note_title,
		custom_fields: custom_fields,
	};
}

/**
 * Takes a line that should represent a credit card and transforms it into a proper secret object
 *
 * @param {[]} item One item of the json
 *
 * @returns {*} The application_password secret object
 */
function transferIntoCreditCard(item: BitwardenItem): ImportedSecret {
	let name = "";
	let credit_card_title = "";
	let credit_card_notes = "";
	let credit_card_name = "";
	let credit_card_number = "";
	const credit_card_pin = "";
	let credit_card_cvc = "";
	let credit_card_valid_through = "";

	if (Object.hasOwn(item, "name") && item["name"] != null) {
		name = item["name"];
		credit_card_title = item["name"];
	}

	if (Object.hasOwn(item, "notes") && item["notes"] != null) {
		credit_card_notes = item["notes"];
	}

	if (Object.hasOwn(item, "card") && item["card"] != null) {
		if (
			Object.hasOwn(item["card"], "cardholderName") &&
			item["card"]["cardholderName"] != null
		) {
			credit_card_name = item["card"]["cardholderName"];
		}

		if (
			Object.hasOwn(item["card"], "number") &&
			item["card"]["number"] != null
		) {
			credit_card_number = item["card"]["number"].replace(/\s/g, "");
		}

		if (
			Object.hasOwn(item["card"], "number") &&
			item["card"]["expMonth"] != null
		) {
			credit_card_valid_through = item["card"]["expMonth"];
		}

		if (
			Object.hasOwn(item["card"], "number") &&
			item["card"]["expYear"] != null
		) {
			credit_card_valid_through =
				credit_card_valid_through + item["card"]["expYear"].slice(-2);
		}

		if (
			Object.hasOwn(item["card"], "number") &&
			item["card"]["expYear"] !== null
		) {
			credit_card_cvc = item["card"]["code"] ?? "";
		}
	}

	if (Object.hasOwn(item, "fields") && item["fields"] != null) {
		for (let i = 0; i < item["fields"].length; i++) {
			credit_card_notes =
				credit_card_notes +
				item["fields"][i]["name"] +
				": " +
				item["fields"][i]["value"] +
				"\n";
		}
	}

	return {
		id: cryptoLibrary.generateUuid(),
		type: "credit_card",
		name: name,
		description: credit_card_number.replace(/.(?=.{4})/g, "x"),
		credit_card_number: credit_card_number,
		credit_card_name: credit_card_name,
		credit_card_cvc: credit_card_cvc,
		credit_card_valid_through: credit_card_valid_through,
		credit_card_pin: credit_card_pin,
		credit_card_notes: credit_card_notes,
		credit_card_title: credit_card_title,
	};
}

/**
 * Takes an item and transforms it into an SSH key entry
 *
 * @param {[]} item One item of the json
 *
 * @returns {*} The ssh_own_key secret object
 */
function transformToSshOwnKey(item: BitwardenItem): ImportedSecret {
	let name = "";
	let ssh_own_key_title = "";
	let ssh_own_key_public = "";
	let ssh_own_key_private = "";
	let ssh_own_key_notes = "";

	if (Object.hasOwn(item, "name") && item["name"] != null) {
		name = item["name"];
		ssh_own_key_title = item["name"];
	}

	if (Object.hasOwn(item, "notes") && item["notes"] != null) {
		ssh_own_key_notes = item["notes"];
	}

	if (Object.hasOwn(item, "sshKey") && item["sshKey"] != null) {
		if (
			Object.hasOwn(item["sshKey"], "publicKey") &&
			item["sshKey"]["publicKey"] != null
		) {
			ssh_own_key_public = item["sshKey"]["publicKey"];
		}

		if (
			Object.hasOwn(item["sshKey"], "privateKey") &&
			item["sshKey"]["privateKey"] != null
		) {
			ssh_own_key_private = item["sshKey"]["privateKey"];
		}

		if (
			Object.hasOwn(item["sshKey"], "keyFingerprint") &&
			item["sshKey"]["keyFingerprint"] != null
		) {
			if (ssh_own_key_notes !== "" && !ssh_own_key_notes.endsWith("\n")) {
				ssh_own_key_notes = ssh_own_key_notes + "\n";
			}
			ssh_own_key_notes =
				ssh_own_key_notes +
				"Key Fingerprint" +
				": " +
				item["sshKey"]["keyFingerprint"];
		}
	}

	return {
		id: cryptoLibrary.generateUuid(),
		type: "ssh_own_key",
		name: name,
		ssh_own_key_title: ssh_own_key_title,
		ssh_own_key_public: ssh_own_key_public,
		ssh_own_key_private: ssh_own_key_private,
		ssh_own_key_notes: ssh_own_key_notes,
	};
}

/**
 * Takes an item and transforms it into a note
 *
 * @param {[]} item One item of the json
 *
 * @returns {*} The secrets object
 */
function transformToTotpCode(item: BitwardenItem): ImportedSecret | null {
	let name = "";
	const totp_notes = "";
	let totp_period = 30;
	let totp_algorithm = "SHA1";
	let totp_digits: string | number = "6";
	let totp_code = "";
	let totp_title = "";

	if (Object.hasOwn(item, "name") && item["name"] !== null) {
		name = item["name"] + " TOTP";
		totp_title = item["name"] + " TOTP";
	}
	if (
		Object.hasOwn(item, "login") &&
		item["login"] != null &&
		Object.hasOwn(item["login"], "totp") &&
		item["login"]["totp"] != null
	) {
		try {
			const parsedTotp = parseTotp(item["login"]["totp"]);
			if (!parsedTotp.secret.base32) return null;
			totp_period =
				parsedTotp instanceof OTPAuth.TOTP ? parsedTotp.period : totp_period;
			totp_algorithm = parsedTotp.algorithm;
			totp_digits = parsedTotp.digits;
			totp_code = parsedTotp.secret.base32;
		} catch (e) {
			return null;
		}
	}

	return {
		id: cryptoLibrary.generateUuid(),
		type: "totp",
		name: name,
		totp_notes: totp_notes,
		totp_code: totp_code,
		totp_digits: totp_digits,
		totp_algorithm: totp_algorithm,
		totp_period: totp_period,
		totp_title: totp_title,
	};
}

/** Collects converted entries and associates each primary secret with its source. */
function gatherSecrets(
	datastore: BitwardenRoot,
	secrets: ImportedSecret[],
	parsedData: BitwardenExport,
	onItem: (
		source: { id?: string; name?: string },
		secret: ImportedSecret,
	) => void,
) {
	let i;
	const folderIndex: Record<string, ImportItemsFolder> = {};
	const folderNameIndex: Record<string, ImportItemsFolder> = {};

	if (parsedData.folders) {
		for (i = 0; i < parsedData["folders"].length; i++) {
			const originalName: string = parsedData["folders"][i]["name"];
			const newFolder: ImportItemsFolder = {
				id: cryptoLibrary.generateUuid(),
				name: originalName,
				items: [],
			};

			folderIndex[parsedData["folders"][i]["id"]] = newFolder;
			folderNameIndex[originalName] = newFolder;

			if (originalName.includes("/")) {
				const actualFolderName = originalName.substr(
					originalName.lastIndexOf("/") + 1,
				);
				const parentFolderName = originalName.substr(
					0,
					originalName.lastIndexOf("/"),
				);
				if (Object.hasOwn(folderNameIndex, parentFolderName)) {
					newFolder["name"] = actualFolderName;
					(folderNameIndex[parentFolderName].folders ??= []).push(newFolder);
				} else {
					// not sure where to put it, so lets treat it like a toplevel folder
					datastore["folders"].push(newFolder);
				}
			} else {
				// toplevel folder
				datastore["folders"].push(newFolder);
			}
		}
	}

	if (parsedData.items) {
		for (i = 0; i < parsedData["items"].length; i++) {
			const detected_type = detect_type(parsedData["items"][i]);
			const contains_totp_code = containsTotp(parsedData["items"][i]);
			const crafted_secrets: ImportedSecret[] = [];

			if (detected_type === "website_password") {
				crafted_secrets.push(
					transformToWebsitePassword(parsedData["items"][i]),
				);
			} else if (detected_type === "credit_card") {
				crafted_secrets.push(transferIntoCreditCard(parsedData["items"][i]));
			} else if (detected_type === "ssh_own_key") {
				crafted_secrets.push(transformToSshOwnKey(parsedData["items"][i]));
			} else {
				crafted_secrets.push(transformToNote(parsedData["items"][i]));
			}

			if (detected_type !== "website_password" && contains_totp_code) {
				const totp = transformToTotpCode(parsedData["items"][i]);
				if (!totp) {
					const value = parsedData.items[i].login?.totp;
					if (value) {
						const secret = crafted_secrets[0];
						const notesField = `${detected_type}_notes` as const;
						const notes = secret[notesField] || "";
						secret[notesField] =
							notes +
							(notes && !notes.endsWith("\n") ? "\n" : "") +
							"TOTP: " +
							value;
					}
				} else {
					const uris = parsedData.items[i].login?.uris ?? [];
					const filter = uris
						.map(({ uri }) => helperService.parseUrl(uri).authority)
						.filter(Boolean)
						.join(", ");
					if (filter) {
						totp.totp_url_filter = filter;
						totp.urlfilter = filter;
					}
					crafted_secrets.push(totp);
				}
			}

			onItem(
				{
					id: parsedData.items[i].id,
					name: parsedData.items[i].name ?? undefined,
				},
				crafted_secrets[0],
			);
			let parent_folder = null;
			const folderId = parsedData.items[i].folderId;
			if (folderId != null && Object.hasOwn(folderIndex, folderId)) {
				parent_folder = folderIndex[folderId];
			}

			for (let iii = 0; iii < crafted_secrets.length; iii++) {
				if (parent_folder === null) {
					datastore["items"].push(crafted_secrets[iii]);
				} else {
					parent_folder["items"].push(crafted_secrets[iii]);
				}
				secrets.push(crafted_secrets[iii]);
			}
		}
	}
}

function parseBitwardenZipJson(
	data: string,
	onItem: (
		source: { id?: string; name?: string },
		secret: ImportedSecret,
	) => void,
): ImportResult<BitwardenRoot> | null {
	const d = new Date();
	const n = d.toISOString();
	const secrets: ImportedSecret[] = [];
	const datastore: BitwardenRoot = {
		id: cryptoLibrary.generateUuid(),
		name: "Import " + n,
		folders: [],
		items: [],
	};
	let parsedData: BitwardenExport;
	try {
		parsedData = JSON.parse(data);
	} catch (err) {
		return null;
	}

	gatherSecrets(datastore, secrets, parsedData, onItem);
	return { datastore: datastore, secrets: secrets };
}
